/**
 * WebSocket State Hook
 * 
 * Provides easy access to WebSocket state and data from Redux.
 * Replaces direct WebSocket usage in components.
 */

import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import {
  selectConnectionState,
  selectIsConnected,
  selectMarketData,
  selectWalletData,
  selectOrderData,
  selectNotifications,
  selectServiceHealth,
  selectTickerBySymbol,
  selectOrderbookBySymbol,
  selectWalletUpdateTrigger,
  selectOrderUpdateTrigger,
  selectUnreadNotificationCount,
  selectIsAnyServerConnected
} from '../redux/websocket';

/**
 * Main WebSocket state hook with null safety
 */
export const useWebSocketState = () => {
  const connectionState = useSelector(selectConnectionState);
  const marketData = useSelector(selectMarketData);
  const walletData = useSelector(selectWalletData);
  const orderData = useSelector(selectOrderData);
  const notifications = useSelector(selectNotifications);
  const serviceHealth = useSelector(selectServiceHealth);
  const isAnyConnected = useSelector(selectIsAnyServerConnected);

  return {
    // Connection states with null safety
    connections: connectionState || {},
    isConnected: isAnyConnected,
    isNotificationsConnected: connectionState?.notifications?.connected || false,
    isTradingConnected: connectionState?.trading?.connected || false,

    // Data with null safety
    marketData: marketData || { tickers: {}, orderbooks: {}, trades: {}, lastUpdate: null },
    walletData: walletData || { balances: {}, transactions: [], lastUpdate: null, updateTrigger: 0 },
    orderData: orderData || { orders: [], orderHistory: [], lastUpdate: null, updateTrigger: 0 },
    notifications: notifications || { messages: [], allNotifications: [], unreadCount: 0, isLoading: false, lastUpdate: null },

    // Service health with null safety
    serviceHealth: serviceHealth || { isHealthy: false, lastHealthCheck: null, errors: [] },
    isHealthy: serviceHealth?.isHealthy || false
  };
};

/**
 * Connection status hook for specific server
 */
export const useConnectionStatus = (serverKey) => {
  return useSelector(state => selectIsConnected(serverKey)(state));
};

/**
 * Market data hook for specific symbol
 */
export const useMarketData = (symbol = null) => {
  const allMarketData = useSelector(selectMarketData);
  const ticker = useSelector(state => symbol ? selectTickerBySymbol(state, symbol) : null);
  const orderbook = useSelector(state => symbol ? selectOrderbookBySymbol(state, symbol) : null);

  return useMemo(() => {
    if (symbol) {
      return {
        ticker,
        orderbook,
        trades: allMarketData.trades[symbol] || [],
        lastUpdate: ticker?.lastUpdate || orderbook?.lastUpdate || null
      };
    }
    
    return allMarketData;
  }, [symbol, ticker, orderbook, allMarketData]);
};

/**
 * Wallet update trigger hook for React Query integration
 */
export const useWalletUpdateTrigger = () => {
  return useSelector(selectWalletUpdateTrigger);
};

/**
 * Order update trigger hook for React Query integration
 */
export const useOrderUpdateTrigger = () => {
  return useSelector(selectOrderUpdateTrigger);
};

/**
 * Notifications hook
 */
export const useNotifications = () => {
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadNotificationCount);

  return {
    messages: notifications.messages,
    unreadCount,
    lastUpdate: notifications.lastUpdate,
    
    // Helper methods
    getUnreadMessages: () => notifications.messages.filter(n => !n.read),
    getMessagesByType: (type) => notifications.messages.filter(n => n.type === type),
    getRecentMessages: (count = 10) => notifications.messages.slice(0, count)
  };
};

/**
 * Service health monitoring hook with null safety
 */
export const useServiceHealth = () => {
  const serviceHealth = useSelector(selectServiceHealth);
  const connections = useSelector(selectConnectionState);

  return useMemo(() => {
    // Add null safety for connections
    const safeConnections = connections || {};
    const safeServiceHealth = serviceHealth || { isHealthy: false, lastHealthCheck: null, errors: [] };

    const connectedServers = Object.entries(safeConnections)
      .filter(([_, state]) => state && state.connected)
      .map(([key, _]) => key);

    const disconnectedServers = Object.entries(safeConnections)
      .filter(([_, state]) => !state || !state.connected)
      .map(([key, state]) => ({ key, error: state?.error || null }));

    return {
      ...safeServiceHealth,
      connectedServers,
      disconnectedServers,
      connectionCount: connectedServers.length,
      hasErrors: (safeServiceHealth.errors || []).length > 0,
      lastError: (safeServiceHealth.errors || [])[0] || null
    };
  }, [serviceHealth, connections]);
};

/**
 * Real-time data hook with automatic updates
 */
export const useRealTimeData = (dataType, symbol = null) => {
  const marketData = useMarketData(symbol);
  const walletData = useSelector(selectWalletData);
  const orderData = useSelector(selectOrderData);

  return useMemo(() => {
    switch (dataType) {
      case 'market':
        return marketData;
      case 'wallet':
        return walletData;
      case 'orders':
        return orderData;
      default:
        return null;
    }
  }, [dataType, marketData, walletData, orderData, symbol]);
};

/**
 * Connection quality hook with null safety
 */
export const useConnectionQuality = () => {
  const connections = useSelector(selectConnectionState);
  const serviceHealth = useSelector(selectServiceHealth);

  return useMemo(() => {
    // Add null safety
    const safeConnections = connections || {};
    const safeServiceHealth = serviceHealth || { isHealthy: false, lastHealthCheck: null, errors: [] };

    const totalServers = Object.keys(safeConnections).length;
    const connectedServers = Object.values(safeConnections).filter(c => c && c.connected).length;
    const connectionRatio = totalServers > 0 ? connectedServers / totalServers : 0;

    let quality = 'poor';
    if (connectionRatio >= 1) {
      quality = 'excellent';
    } else if (connectionRatio >= 0.5) {
      quality = 'good';
    } else if (connectionRatio > 0) {
      quality = 'fair';
    }

    const hasRecentErrors = (safeServiceHealth.errors || []).some(
      error => error && Date.now() - error.timestamp < 60000 // Last minute
    );

    return {
      quality,
      connectionRatio,
      connectedCount: connectedServers,
      totalCount: totalServers,
      hasRecentErrors,
      isStable: connectionRatio >= 0.5 && !hasRecentErrors,
      lastHealthCheck: safeServiceHealth.lastHealthCheck
    };
  }, [connections, serviceHealth]);
};

/**
 * WebSocket metrics hook for debugging with null safety
 */
export const useWebSocketMetrics = () => {
  const connections = useSelector(selectConnectionState);
  const marketData = useSelector(selectMarketData);
  const walletData = useSelector(selectWalletData);
  const orderData = useSelector(selectOrderData);
  const notifications = useSelector(selectNotifications);

  return useMemo(() => {
    const now = Date.now();

    // Add null safety
    const safeConnections = connections || {};
    const safeMarketData = marketData || { tickers: {}, orderbooks: {}, trades: {}, lastUpdate: null };
    const safeWalletData = walletData || { balances: {}, transactions: [], lastUpdate: null, updateTrigger: 0 };
    const safeOrderData = orderData || { orders: [], orderHistory: [], lastUpdate: null, updateTrigger: 0 };
    const safeNotifications = notifications || { messages: [], allNotifications: [], unreadCount: 0, isLoading: false, lastUpdate: null };

    return {
      connections: {
        total: Object.keys(safeConnections).length,
        connected: Object.values(safeConnections).filter(c => c && c.connected).length,
        reconnectAttempts: Object.values(safeConnections).reduce((sum, c) => sum + (c?.reconnectAttempts || 0), 0)
      },

      dataFreshness: {
        market: safeMarketData.lastUpdate ? now - safeMarketData.lastUpdate : null,
        wallet: safeWalletData.lastUpdate ? now - safeWalletData.lastUpdate : null,
        orders: safeOrderData.lastUpdate ? now - safeOrderData.lastUpdate : null,
        notifications: safeNotifications.lastUpdate ? now - safeNotifications.lastUpdate : null
      },

      dataVolume: {
        tickers: Object.keys(safeMarketData.tickers || {}).length,
        orderbooks: Object.keys(safeMarketData.orderbooks || {}).length,
        trades: Object.values(safeMarketData.trades || {}).reduce((sum, trades) => sum + (trades?.length || 0), 0),
        walletBalances: Object.keys(safeWalletData.balances || {}).length,
        orders: (safeOrderData.orders || []).length,
        orderHistory: (safeOrderData.orderHistory || []).length,
        notifications: (safeNotifications.messages || []).length
      },

      updateTriggers: {
        wallet: safeWalletData.updateTrigger || 0,
        orders: safeOrderData.updateTrigger || 0
      }
    };
  }, [connections, marketData, walletData, orderData, notifications]);
};

export default useWebSocketState;
